import { useMutation } from "@apollo/client";
import { MenuItem, MenuItemProps, Spinner } from "@chakra-ui/react";
import { MdArchive } from "react-icons/md";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { useActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { assertPresence } from "utils/assertions";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";

const fragment = gql(/* GraphQL */ `
  fragment RetailerArchiveMenuItemFragment on Retailer {
    id
    isArchived
    isCustom
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation ArchiveRetailer($retailerId: UUID!, $isArchived: Boolean!) {
    updateRetailer(
      input: { id: $retailerId, patch: { isArchived: $isArchived } }
    ) {
      retailer {
        id
        isArchived
        nameWithUniqueSuffix
        ...RetailerArchiveMenuItemFragment
      }
    }
  }
`);

type Props = Omit<MenuItemProps, "onClick"> & {
  isAdmin?: boolean;
  retailer: FragmentType<typeof fragment> | null | undefined;
};

export default function ArchiveMenuItem({
  isAdmin,
  isDisabled,
  retailer: retailerFragment,
  ...rest
}: Props) {
  const retailer = getFragmentData(fragment, retailerFragment);

  const { showSuccessToast, showErrorToast } = useAsyncCompleteToast();

  const brandContext = useActiveBrandIdContext();

  const [performUpdate, { loading }] = useMutation(mutation, {
    context: isAdmin ? ADMIN_CONTEXT : brandContext,
  });

  const handleClick = () => {
    assertPresence(retailer?.id);
    performUpdate({
      variables: { retailerId: retailer.id, isArchived: !retailer.isArchived },
    })
      .then((result) => {
        const updatedRetailer = result.data?.updateRetailer?.retailer;
        assertPresence(updatedRetailer);
        showSuccessToast(
          `Successfully ${
            updatedRetailer.isArchived ? "archived" : "unarchived"
          } “${updatedRetailer.nameWithUniqueSuffix}”`,
        );
      })
      .catch(showErrorToast);
  };

  return (
    <MenuItem
      icon={loading ? <Spinner size="sm" color="gray" /> : <MdArchive />}
      isDisabled={isDisabled || !retailer || loading}
      onClick={handleClick}
      {...rest}
    >
      {retailer?.isArchived ? "Unarchive" : "Archive"}{" "}
      {retailer?.isCustom ? "Custom" : "Default"} Retailer
    </MenuItem>
  );
}
